/**
 * The catalog's enumerations, as things a person can pick.
 *
 * Each one is a `Record<TheUnion, string>`, which is the point: TypeScript checks a record
 * for exhaustiveness, so adding a `Layout` to `@atlas-nook/mediums` and forgetting the admin
 * form is a **build error** rather than a select box that quietly cannot express the new
 * value. The keys are the stored values; the strings are only what an admin reads.
 */

import type {
  Availability,
  CompositionInput,
  ConsistencyRisk,
  CutterDependency,
  FrameModel,
  Layout,
  MediumFamily,
  NarrativeStrength,
  OutputKind,
  PieceRole,
  ProductionEase,
  RefillModel,
  RenderMode,
  SelectStrategy,
  YoungSafe,
} from '@atlas-nook/mediums';
import type { PrimitiveKind } from '@atlas-nook/story';

export const FAMILY: Record<MediumFamily, string> = {
  book: 'Book',
  card: 'Card',
  theatre: 'Theatre',
  optical: 'Optical',
  motion: 'Motion',
  other: 'Other',
  digital: 'Digital',
};

export const AVAILABILITY: Record<Availability, string> = {
  concept: 'Concept',
  prototype: 'Prototype',
  available: 'Available',
};

export const YOUNG_SAFE: Record<YoungSafe, string> = {
  ok: 'OK',
  caution: 'Caution',
  no: 'No',
};

/** `null` is a real value here: tier is null exactly when the medium is ruled out. */
export const TIER: Record<string, string> = {
  '0': '0 · the base',
  '1': '1 · lead',
  '2': '2 · strong, with a caveat',
  '3': '3 · novelty / add-on',
  none: 'none · ruled out',
};

export const PRODUCTION_EASE: Record<ProductionEase, string> = {
  high: 'High',
  med: 'Medium',
  low: 'Low',
};

export const CUTTER: Record<CutterDependency, string> = {
  none: 'None',
  helpful: 'Helpful',
  required: 'Required',
};

export const RISK: Record<ConsistencyRisk, string> = {
  low: 'Low',
  med: 'Medium',
  high: 'High',
};

export const REFILL: Record<RefillModel, string> = {
  strong: 'Strong',
  possible: 'Possible',
  no: 'No',
};

export const NARRATIVE: Record<NarrativeStrength, string> = {
  high: 'High',
  med: 'Medium',
  low: 'Low',
};

export const FRAME_MODEL: Record<FrameModel, string> = {
  'discrete-scenes': 'Discrete scenes',
  silhouette: 'Silhouette',
  panorama: 'Panorama',
  'two-image': 'Two images',
  'motion-frames': 'Motion frames',
  other: 'Other',
};

export const LAYOUT: Record<Layout, string> = {
  sequence: 'sequence — N scenes → N ordered units',
  depth: 'depth — one scene → layered planes',
  stage: 'stage — backdrop + movable figures',
  panorama: 'panorama — N scenes → one strip',
  pair: 'pair — two images → a two-sided disc',
  figure: 'figure — one character → jointed pieces',
  tokens: 'tokens — props → small discrete pieces',
  frames: 'frames — many motion frames',
  film: 'film — N scenes → one animated video',
};

export const COMPOSITION_INPUT: Record<CompositionInput, string> = {
  flat: 'flat — the single composed image',
  layers: 'layers — the separable layer stack',
};

export const RENDER_MODE: Record<RenderMode, string> = {
  'full-colour': 'Full colour',
  silhouette: 'Silhouette',
};

export const SELECT_STRATEGY: Record<SelectStrategy, string> = {
  all: 'all',
  'first-n': 'first-n',
  hero: 'hero',
  props: 'props',
  character: 'character',
};

export const PIECE_ROLE: Record<PieceRole, string> = {
  backdrop: 'Backdrop',
  character: 'Character',
  prop: 'Prop',
  foreground: 'Foreground',
};

export const OUTPUT_KIND: Record<OutputKind, string> = {
  page: 'Page',
  card: 'Card',
  plane: 'Plane',
  strip: 'Strip',
  disc: 'Disc',
  figure: 'Figure',
  token: 'Token',
  frame: 'Frame',
  'cut-file': 'Cut file',
  video: 'Video',
};

export const PRIMITIVE_KIND: Record<PrimitiveKind, string> = {
  character: 'Character',
  setting: 'Setting',
  prop: 'Prop',
  beat: 'Beat',
};

/** `Object.entries` over a record, typed so a select's `value` stays the union member. */
export function entriesOf<T extends string>(record: Record<T, string>): [T, string][] {
  return Object.entries(record) as [T, string][];
}
